// Scale Plus team workspace guests on app.groove.cm.
//
// ScalePlus sends an invited member to /scale-workspace/callback with a
// single-use launch code. The dashboard hands that code to gd-apis, which
// exchanges it with the hub, checks the signed-in member, and keeps the guest
// session on the server. The browser only remembers display fields (whose
// workspace, which apps) for the banner and client-side routing. Nothing kept
// here grants access: gd-apis re-authorizes every guest request with the hub.

export const WORKSPACE_CALLBACK_PATH = '/scale-workspace/callback';
export const WORKSPACE_GUEST_KEY = 'scale_workspace_guest';
export const SCALEPLUS_TEAM_URL = 'https://app.scaleplus.gg/team';

const GROOVE_SLUGS = [
  'groovecm',
  'groovepages',
  'groovesell',
  'groovemail',
  'groovemember',
  'groovevideo',
  'grooveblog',
  'groovewebinar'
];

const FAILURES = {
  invalid_or_expired_code: {
    title: 'This workspace link has expired',
    message: 'Workspace links from Scale Plus work once and only for a few minutes. Open Groove.cm again from your Scale Plus team workspace.'
  },
  workspace_actor_mismatch: {
    title: 'This workspace link belongs to someone else',
    message: 'You are signed in to Groove.cm as a different person than the one Scale Plus sent. Sign out, sign in with your invited account, and open Groove.cm again from Scale Plus.'
  },
  workspace_owner_unavailable: {
    title: 'This workspace has no Groove.cm account yet',
    message: 'The workspace owner has not used Groove.cm with this login yet, so there is nothing to open. Ask them to open Groove.cm once from Scale Plus, then try again.'
  },
  workspace_actor_missing: {
    title: 'Sign in to continue',
    message: 'Sign in with the account you were invited with, then open Groove.cm again from Scale Plus.'
  },
  default: {
    title: 'We could not open this workspace',
    message: 'Groove.cm could not confirm your access to this workspace right now. Open Groove.cm again from your Scale Plus team workspace in a moment.'
  }
};

export function workspaceFailure(reason) {
  return FAILURES[reason] || FAILURES.default;
}

/** The launch code and requested return path, or null on any other page. */
export function readWorkspaceCallback(location) {
  if (!location || location.pathname !== WORKSPACE_CALLBACK_PATH) return null;
  const params = new URLSearchParams(location.search || '');
  const code = (params.get('scale_workspace_code') || '').trim();
  return {
    code: code && code.length <= 256 ? code : '',
    returnPath: params.get('return_path')
  };
}

/** A same-origin path to land on after the exchange; anything else is dropped. */
export function safeDashboardPath(path) {
  if (typeof path !== 'string' || path === '' || path.length > 512) return null;
  if (path[0] !== '/' || path.startsWith('//') || path.includes('\\')) return null;
  if (/[\u0000-\u001f\u007f]/.test(path)) return null;
  if (path === WORKSPACE_CALLBACK_PATH || path.startsWith(`${WORKSPACE_CALLBACK_PATH}?`)) return null;
  return path;
}

function grooveSlugs(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((slug, index) => GROOVE_SLUGS.includes(slug) && value.indexOf(slug) === index);
}

function text(value) {
  return typeof value === 'string' ? value.trim().slice(0, 120) : '';
}

export function writeWorkspaceGuest(storage, actorSubject, data) {
  if (!storage || !actorSubject || !data) return null;
  const guest = {
    actor: String(actorSubject),
    workspaceName: text(data.workspace && data.workspace.displayName),
    ownerName: text(data.owner && data.owner.displayName),
    allowedApplications: grooveSlugs(data.allowedApplications),
    status: data.status === 'unverified' ? 'unverified' : 'active'
  };
  storage.setItem(WORKSPACE_GUEST_KEY, JSON.stringify(guest));
  return guest;
}

/** The stored guest display state for this signed-in member, or null. */
export function readWorkspaceGuest(storage, actorSubject) {
  if (!storage) return null;
  let guest = null;
  try {
    guest = JSON.parse(storage.getItem(WORKSPACE_GUEST_KEY) || 'null');
  } catch (error) {
    return null;
  }
  if (!guest || typeof guest !== 'object') return null;
  if (!actorSubject || guest.actor !== String(actorSubject)) return null;
  return {
    actor: guest.actor,
    workspaceName: text(guest.workspaceName),
    ownerName: text(guest.ownerName),
    allowedApplications: grooveSlugs(guest.allowedApplications),
    status: guest.status === 'unverified' ? 'unverified' : 'active'
  };
}

export function clearWorkspaceGuest(storage) {
  if (storage) storage.removeItem(WORKSPACE_GUEST_KEY);
}

/**
 * A guest works in the owner's paid account, so the member's own free-account
 * lockout must not send them to the upgrade page. Display only: gd-apis still
 * applies the owner's policy to every guest request.
 */
export function relaxAccessPolicyForGuest(storage) {
  if (!storage) return false;
  let user = null;
  try {
    user = JSON.parse(storage.getItem('user') || 'null');
  } catch (error) {
    return false;
  }
  if (!user || !user.access_policy || !user.access_policy.restricted) return false;
  user.access_policy = { ...user.access_policy, restricted: false, reason: 'scale_workspace_guest' };
  storage.setItem('user', JSON.stringify(user));
  return true;
}

function errorReason(error) {
  const data = error && error.response && error.response.data;
  return (data && typeof data === 'object' && (data.error || data.reason)) || '';
}

/**
 * Finishes the ScalePlus launch on /scale-workspace/callback.
 *
 * @returns {Promise<{status: 'redirect'|'login'|'failed', url?: string, reason?: string}>}
 */
export async function completeWorkspaceCallback({ http, storage, callback, actorSubject, isAuthenticated, login }) {
  if (!callback || !callback.code) {
    return { status: 'failed', reason: 'invalid_or_expired_code' };
  }
  if (!isAuthenticated()) {
    // Keycloak returns to this exact URL, code included, after sign-in.
    await login();
    return { status: 'login' };
  }

  try {
    const response = await http.post('/scale-workspace/exchange', { code: callback.code });
    const data = response && response.data;
    if (!data || data.guest !== true) {
      return { status: 'failed', reason: 'default' };
    }
    writeWorkspaceGuest(storage, actorSubject, data);
    relaxAccessPolicyForGuest(storage);
    const url = safeDashboardPath(data.returnPath) || safeDashboardPath(callback.returnPath) || '/';
    return { status: 'redirect', url };
  } catch (error) {
    return { status: 'failed', reason: errorReason(error) || 'default' };
  }
}

/**
 * Re-reads the member's workspace from gd-apis (which confirms it with the
 * hub) and updates the stored display state. Ended sessions are cleared.
 */
export async function refreshWorkspaceGuest({ http, storage, actorSubject }) {
  try {
    const response = await http.get('/scale-workspace/context');
    const data = response && response.data;
    if (data && data.guest === true) {
      const guest = writeWorkspaceGuest(storage, actorSubject, data);
      relaxAccessPolicyForGuest(storage);
      return guest;
    }
    clearWorkspaceGuest(storage);
    return null;
  } catch (error) {
    // Keep what we had; gd-apis denies guest requests on its own if needed.
    return readWorkspaceGuest(storage, actorSubject);
  }
}

export async function leaveWorkspace({ http, storage }) {
  try {
    await http.post('/scale-workspace/leave', {});
  } finally {
    clearWorkspaceGuest(storage);
  }
}

/** Plain failure page for the callback, built without innerHTML. */
export function renderWorkspaceFailure(doc, reason) {
  const failure = workspaceFailure(reason);
  const root = doc.getElementById('app') || doc.body;
  while (root.firstChild) root.removeChild(root.firstChild);

  const wrap = doc.createElement('div');
  wrap.setAttribute('role', 'alert');
  wrap.style.cssText = 'max-width:520px;margin:15vh auto 0;padding:0 16px;font-family:"Nunito Sans",system-ui,sans-serif;color:#1f2937;text-align:center;';

  const title = doc.createElement('h1');
  title.textContent = failure.title;
  title.style.cssText = 'font-size:22px;font-weight:700;margin:0 0 12px;';

  const message = doc.createElement('p');
  message.textContent = failure.message;
  message.style.cssText = 'font-size:15px;line-height:1.6;margin:0 0 24px;color:#4b5563;';

  const back = doc.createElement('a');
  back.href = SCALEPLUS_TEAM_URL;
  back.textContent = 'Back to Scale Plus';
  back.style.cssText = 'display:inline-block;padding:10px 18px;border-radius:8px;background:#2563eb;color:#fff;text-decoration:none;font-weight:600;';

  const own = doc.createElement('a');
  own.href = '/';
  own.textContent = 'Open my own Groove.cm';
  own.style.cssText = 'display:inline-block;margin-left:12px;padding:10px 18px;color:#2563eb;text-decoration:none;font-weight:600;';

  wrap.appendChild(title);
  wrap.appendChild(message);
  wrap.appendChild(back);
  wrap.appendChild(own);
  root.appendChild(wrap);
  return failure;
}
